import * as Sentry from '@sentry/browser';
import type { ScopeContext, SeverityLevel } from '@sentry/types';
import type { GraphQLError } from 'graphql';
import { shouldIgnoreError, shouldIgnoreGraphQLError } from './utils';

type CaptureContext = Partial<ScopeContext>;

export const sentry = {
  captureGraphQLError: (error: GraphQLError, captureContext?: CaptureContext) => {
    if (shouldIgnoreGraphQLError(error)) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setContext('GraphQLError', {
        ...(error.path && { path: error.path }),
        code: error.extensions?.code,
      });
      scope.setFingerprint(
        [
          error.message,
          error.extensions?.code,
          ...(error.path ? error.path.map(String) : []),
        ].filter(Boolean) as string[],
      );

      scope.captureMessage(error.message, 'error', { captureContext });
    });
  },
  captureException: (
    exception: any,
    captureContext?: CaptureContext & { componentStack?: string | null },
  ) => {
    const error =
      exception instanceof Error
        ? exception
        : new Error((exception as Error)?.message || 'Unknown error');

    if (shouldIgnoreError(error)) {
      return;
    }

    Sentry.withScope((scope) => {
      if (captureContext?.componentStack) {
        // Copied from https://github.com/getsentry/sentry-javascript/blob/master/packages/react/src/errorboundary.tsx
        const errorBoundaryError = new Error(error.message);

        errorBoundaryError.name = `ReactErrorBoundary: ${errorBoundaryError.name}`;
        errorBoundaryError.stack = captureContext.componentStack;
        error.cause = errorBoundaryError; // eslint-disable-line no-param-reassign
      }

      scope.captureException(error, { originalException: error, captureContext });
    });
  },
  captureMessage: (message: string, level?: SeverityLevel, captureContext?: CaptureContext) => {
    Sentry.withScope((scope) => {
      scope.captureMessage(message, level, { captureContext });
    });
  },
};
